<template>
    <div
        class="voc-item-card"
        :class="{
            'voc-item-card--arrowActive': showEnterIcon,
            'voc-item-card--hover': hasHover,
            'voc-item-card--border': hasBorder,
            [`voc-item-card--${type}`]: true,
            [`voc-item-card--${size}`]: true,
        }"
        data-cy="voc-item-card"
        v-on="bindedActions">
        <div
            class="voc-item-card__header"
            data-cy="voc-item-card-header">
            <pendo-loading-indicator
                v-if="loading"
                type="skeleton"
                :skeleton-props="{ height: 24 }"
                loading />
            <template v-else>
                <explore-type-label
                    :type="itemType"
                    size="medium" />
                <pendo-divider
                    v-if="formattedData.date"
                    direction="vertical"
                    decorative
                    :stroke-width="2"
                    width="16px"
                    height="14px" />
                <span v-if="formattedData.date">{{ formattedData.date }}</span>
                <template v-if="formattedData.feedbackType">
                    <pendo-divider
                        direction="vertical"
                        decorative
                        :stroke-width="2"
                        width="16px"
                        height="14px" />
                    <feedback-type-tag
                        :type="formattedData.feedbackType"
                        size="medium" />
                </template>
                <div
                    v-if="actions.length"
                    class="voc-item-card__header-actions"
                    data-cy="voc-item-card-header-actions">
                    <pendo-button
                        v-for="action in actions"
                        :key="action.id"
                        :icon="action.icon"
                        :label="action.label"
                        :data-cy="`voc-item-card-action-${action.id}`"
                        type="subtle"
                        size="mini"
                        icon-size="14"
                        @click.stop="$emit('action', { action, item })" />
                </div>
            </template>
        </div>
        <div
            v-if="loading || formattedData.title || formattedData.description || formattedData.keyQuote"
            class="voc-item-card__body"
            data-cy="voc-item-card-body">
            <pendo-loading-indicator
                v-if="loading"
                type="skeleton"
                :skeleton-props="{ height: 24 }"
                loading />
            <template v-else>
                <span
                    class="voc-item-card__title"
                    data-cy="voc-item-card-title">
                    {{ formattedData.title }}
                </span>
            </template>
            <pendo-loading-indicator
                v-if="loading && (formattedData.keyQuote || formattedData.description)"
                type="skeleton"
                :skeleton-props="{ height: 48 }"
                loading />
            <template v-else>
                <div
                    v-if="formattedData.keyQuote"
                    class="voc-item-card__key-quote">
                    <pendo-icon
                        type="quote-start"
                        size="16" />
                    <span class="voc-item-card__key-quote-text">{{ formattedData.keyQuote }}</span>
                </div>
                <div
                    v-if="!formattedData.keyQuote && formattedData.description"
                    class="voc-item-card__description">
                    {{ formattedData.description }}
                </div>
            </template>
        </div>
        <div
            v-if="footerData.visible.length"
            class="voc-item-card__footer"
            data-cy="voc-item-card-footer">
            <pendo-loading-indicator
                v-if="loading"
                type="skeleton"
                :skeleton-props="{ height: 24 }"
                loading />
            <template v-else>
                <template
                    v-for="(component, index) in footerData.visible"
                    :key="component.id">
                    <pendo-divider
                        v-if="index > 0"
                        decorative
                        direction="vertical"
                        :stroke-width="2"
                        width="16px"
                        height="14px" />
                    <component
                        :is="component.component"
                        v-bind="component.props" />
                </template>
                <template v-if="footerData.hidden.length">
                    <pendo-divider
                        decorative
                        direction="vertical"
                        :stroke-width="2"
                        width="16px"
                        height="14px" />
                    <pendo-popover-v2
                        arrow
                        placement="bottom"
                        class="voc-item-card__footer-hidden-popover">
                        <span class="voc-item-card__footer-hidden">{{ $t('common.Plus_count__more', { count: footerData.hidden.length }) }}</span>
                        <template #popover>
                            <div class="voc-item-card__footer-hidden-content">
                                <component
                                    :is="component.component"
                                    v-for="component in footerData.hidden"
                                    :key="`footer-hidden-${component.id}`"
                                    v-bind="component.props" />
                            </div>
                        </template>
                    </pendo-popover-v2>
                </template>
            </template>
        </div>
    </div>
</template>

<script>
import AccountLabel from '@/common/account-label/account-label.vue';
import AppLabel from '@/common/app-label/app-label.vue';
import IconLabel from '@/common/icon-label/icon-label.vue';
import ProductAreaLabel from '@/common/product-area-label/product-area-label.vue';
import ExploreTypeLabel from '@/voc/components/explore-type-label/explore-type-label.vue';
import FeedbackSource from '@/voc/components/feedback-source/feedback-source.vue';
import FeedbackTypeTag from '@/voc/components/feedback-type-tag/feedback-type-tag.vue';
import VoteStrengthLabel from '@/voc/components/vote-strength-label/vote-strength-label.vue';
import { getOnBehalfOfDisplayableName } from '@/voc/helpers/common.helper';
import { PendoButton, PendoDivider, PendoIcon, PendoLoadingIndicator, PendoPopoverV2 } from '@pendo/components';
import moment, { DATE_FORMAT } from '@utils/moment-wrapper';

export default {
    name: 'VocItemCard',
    components: {
        ExploreTypeLabel,
        PendoIcon,
        PendoDivider,
        PendoButton,
        IconLabel,
        FeedbackSource,
        AccountLabel,
        FeedbackTypeTag,
        PendoLoadingIndicator,
        PendoPopoverV2,
        VoteStrengthLabel,
        AppLabel,
    },
    props: {
        item: {
            type: Object,
            required: true,
        },
        type: {
            type: String,
            default: 'card',
        },
        showEnterIcon: {
            type: Boolean,
            default: false,
        },
        handleClick: {
            type: Boolean,
            default: false,
        },
        hasHover: {
            type: Boolean,
            default: false,
        },
        size: {
            type: String,
            default: 'medium',
            validator: (value) => ['small', 'medium'].includes(value),
        },
        hasBorder: {
            type: Boolean,
            default: false,
        },
        actions: {
            type: Array,
            default: () => [],
        },
        disableAccountHover: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },

    },
    emits: ['action', 'click'],
    computed: {
        hasProductAreas () {
            return this.item.sourceExpanded?.productAreaIds?.length > 0 || this.item.productAreaIds?.length > 0;
        },
        hasApps () {
            return this.item.sourceExpanded?.appIds?.length > 0 || this.item.appIds?.length > 0;
        },
        bindedActions () {
            return {
                ...(this.handleClick && { 'click': ($event) => { $event.stopPropagation(); this.$emit('click'); } }),
            };
        },
        isVote () {
            return this.item.revoked !== undefined;
        },
        itemType () {
            let feedbackType = this.isVote ? this.item.source.toLowerCase() : this.formattedData.type;
            if(feedbackType === 'insight') {
                feedbackType = 'highlight';
            }
            if(feedbackType === 'ideadetail') {
                feedbackType = 'proxy';
            }

            return feedbackType;
        },
        formattedData () {
            const vocItem = this.item.sourceExpanded || this.item;
            const onBehalfOf = this.item.onBehalfOf ? this.item : vocItem;
            const createdByName = getOnBehalfOfDisplayableName(onBehalfOf);
            const title = vocItem.formData?.length ? vocItem.formData[0].content : vocItem.title || null;
            const description = vocItem.formData?.length ? vocItem.formData.slice(1).map((item) => item.content).join(' ') : vocItem.description || null;
            const type = vocItem.type || 'feedback';

            return {
                title,
                description,
                type,
                createdByName,
                date: this.item.createdAt ? moment(this.item.createdAt).format(DATE_FORMAT.short) : null,
                account: this.item.account || { id: this.item.accountId, name: this.item.accountName },
                sourceName: vocItem.sourceName,
                feedbackType: vocItem.feedbackType,
                keyQuote: vocItem.keyQuote || this.item.comment || null,
                voteStrength: this.item.strength || null,
            };
        },
        footerData () {
            const footerItems = {
                ...(this.formattedData.account?.id && { account: {
                    id: 'account-label',
                    component: AccountLabel,
                    props: {
                        account: this.formattedData.account,
                        showIcon: true,
                        showLabel: true,
                        hasHover: !this.disableAccountHover,
                    },
                } }),
                ...(this.hasApps && { apps: {
                    id: 'app-label',
                    component: AppLabel,
                    props: {
                        app: this.item.sourceExpanded?.appIds || this.item.appIds,
                        size: 'medium',
                        showIcon: true,
                        showLabel: true,
                        showMax: 1,
                    },
                } }),
                ...(this.formattedData.createdByName && { createdByName: {
                    id: 'created-by-name',
                    component: IconLabel,
                    props: {
                        icon: 'user',
                        label: this.formattedData.createdByName,
                        size: 'medium',
                        showIcon: true,
                        showLabel: true,
                    },
                } }),
                ...(this.formattedData.sourceName && { sourceName: {
                    id: 'feedback-source',
                    component: FeedbackSource,
                    props: {
                        type: this.formattedData.sourceName,
                        showLabel: true,
                        size: 'medium',
                    },
                } }),
                ...(this.hasProductAreas && { productAreas: {
                    id: 'product-area',
                    component: ProductAreaLabel,
                    props: {
                        productArea: this.item.sourceExpanded?.productAreaIds || this.item.productAreaIds,
                        size: 'medium',
                        showIcon: true,
                        showLabel: true,
                    },
                } }),
                ...(this.formattedData.voteStrength && { voteStrength: {
                    id: 'vote-strength',
                    component: VoteStrengthLabel,
                    props: {
                        voteStrength: this.formattedData.voteStrength,
                        size: 'medium',
                        showIcon: true,
                        showLabel: true,
                    },
                } }),
            };
            const footerItemsByType = this.footerItemsByType[this.itemType] || this.footerItemsByType.default;
            const visible = footerItemsByType.visible.reduce((acc, item) => {
                if(footerItems[item]) {
                    acc.push(footerItems[item]);
                }

                return acc;
            }, []);
            const hidden = footerItemsByType.hidden.reduce((acc, item) => {
                if(footerItems[item]) {
                    acc.push(footerItems[item]);
                }

                return acc;
            }, []);

            return {
                visible,
                hidden,
            };
        },
        footerItemsByType () {
            return {
                feedback: {
                    visible: [
                        'account',
                        ...(this.isVote ? [] : ['apps']),
                        'productAreas',
                        ...(this.isVote ? ['voteStrength'] : []),
                    ],
                    hidden: [
                        'createdByName',
                        'sourceName',
                    ],
                },
                highlight: {
                    visible: [
                        'account',
                        ...(this.isVote ? [] : ['apps']),
                        'productAreas',
                        ...(this.isVote ? ['voteStrength'] : []),
                    ],
                    hidden: [
                        'createdByName',
                        'sourceName',
                    ],
                },
                default: {
                    visible: [
                        'account',
                        'createdByName',
                        ...(this.isVote ? ['voteStrength'] : []),
                    ],
                    hidden: [],
                },
            };
        },
    },
};
</script>

<style lang="less" scoped>
@import '@/voc/styles/voc.style.less';

.voc-item-card {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin: 0;
    padding: 16px;
    max-width: 100%;
    font-size: 12px;

    &--arrowActive,
    &--hover:hover {
        background-color: var(--palette-gray-20);
    }

    &--border {
        border: 1px solid var(--palette-gray-40);
    }

    &__header {
        display: flex;
        align-items: center;
        min-width: 0;

        :deep(.app.pendo-button--mini) {
            height: 16px;
            font-size: 12px;
            line-height: 16px;
        }
    }

    &__header-actions {
        flex-shrink: 0;
        flex-grow: 0;
        margin-left: auto;
    }

    &__header > span {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        flex-shrink: 1;
    }

    &__header > explore-type-label {
        flex-shrink: 1;
        min-width: 0;
    }

    &__body {
        display: flex;
        flex: 1;
        flex-direction: column;
        overflow: hidden;
        gap: 2px;
    }

    &__title {
        font-weight: 600;
        font-size: 14px;
        line-height: 150%;
        margin: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    &__description {
        display: -webkit-box;
        font-weight: 400;
        font-size: 14px;
        line-clamp: 2;
        -webkit-line-clamp: 2;
        text-overflow: ellipsis;
        word-wrap: break-word;
        white-space: normal;
        overflow: hidden;
        -webkit-box-orient: vertical;
        line-height: 150%;
    }
    &__key-quote {
        display: flex;
        align-items: center;
        border-left: 2px solid var(--palette-gray-50);
        font-weight: 400;
        font-size: 14px;
        padding: 4px 0 4px 8px;
        gap: 8px;
        white-space: pre-wrap;

        :deep(.pendo-icon) {
            color: var(--palette-gray-70);
        }

        &-text {
            display: -webkit-box;
            line-clamp: 2;
            -webkit-line-clamp: 2;
            text-overflow: ellipsis;
            word-wrap: break-word;
            white-space: normal;
            overflow: hidden;
            -webkit-box-orient: vertical;
            line-height: 110%;
        }
    }

    &__data {
        display: flex;
        align-items: center;
        font-weight: 400;
        font-size: 12px;
        line-height: 150%;
        color: var(--palette-gray-70);
        margin: 0;
        overflow: hidden;
        max-width: 100%;

        & > span {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
    }

    &__actions {
        margin-left: auto;
        display: flex;
        align-items: center;
        gap: 3px;
        flex-shrink: 0;
        flex-grow: 0;
    }

    &__openIcon {
        margin-left: auto;
        padding: 5px;
        border-radius: 4px;
        z-index: 1000000;

        &:hover {
            background-color: var(--palette-gray-40);
        }
    }
    &__enterIcon {
        background-color: var(--palette-gray-0);
        border-radius: 2px;
        border: 1px solid var(--palette-gray-40);
        padding: 3px;
        opacity: 0;

        &--hover {
            opacity: 1;
        }
    }
    &__footer {
        display: flex;
        align-items: center;
        min-width: 0;

        :deep(.pendo-app-display) {
            line-height: 18px;
        }
    }
    &__app-product-area {
        overflow: hidden;
    }
    &__footer-hidden {
        display: flex;
        align-items: center;
        gap: 8px;
        color: var(--palette-gray-70);
        cursor: pointer;
    }
    :deep(.pendo-popover-v2__reference) {
        flex-shrink: 0;
        flex-grow: 0;
    }
}

:deep(.pendo-divider) {
    flex-shrink: 0;
    flex-grow: 0;
}

:deep(.pendo-button .icon-prefix) {
    padding-right: 4px;
}
:deep(.pendo-button + .pendo-button) {
    margin-left: 16px;
}
</style>

<style>
.voc-item-card__footer-hidden-content {
    display: flex;
    flex-direction: column;
    gap: 8px;
}
</style>
